/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {DropdownHelperService} from '../../service/dropdown-helper.service';

@Component({
    selector: 'app-dropdown-with-description-cell-renderer',
    template: `
        <div class="dropdown-cell-content">
          <div class="dropdown-value">{{displayValue}}</div>
          @if (description) {
            <div class="dropdown-description">{{description}}</div>
          }
        </div>
        `,
    styles: [`
        .dropdown-cell-content {
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-height: 100%;
            padding: 4px 8px;
            box-sizing: border-box;
        }
        .dropdown-value {
            line-height: 1.3;
            word-wrap: break-word;
        }
        .dropdown-description {
            font-size: 0.85em;
            opacity: 40%;
            line-height: 1.3;
            margin-top: 2px;
            word-wrap: break-word;
        }
    `],
    standalone: false
})
export class DropdownWithDescriptionCellRendererComponent implements ICellRendererAngularComp {
    public displayValue: string = '';
    public description: string = '';
    private allowedValues: any[] = [];
    private isListManagerStructure: boolean = false;
    private allowedValueKey?: string;

    agInit(params: any): void {
        this.refresh(params);
    }

    refresh(params: any): boolean {
        // Use valueFormatted if available (result of valueFormatter), otherwise use raw value
        // This ensures we get the formatted string value instead of the raw object
        const value = params.valueFormatted != null ? params.valueFormatted : params.value;
        const rawValue = params.value; // Keep raw value for validation against allowedValues
        // Get parameters from cellRendererParams (preferred) or cellEditorParams (fallback)
        this.allowedValues = params.colDef?.cellRendererParams?.allowedValues || 
                            params.colDef?.cellEditorParams?.allowedValues || [];
        this.isListManagerStructure = params.colDef?.cellRendererParams?.isListManagerStructure || 
                                     params.colDef?.cellEditorParams?.isListManagerStructure || false;
        this.allowedValueKey = params.colDef?.cellRendererParams?.allowedValueKey || 
                               params.colDef?.cellEditorParams?.allowedValueKey;

        if (value == null || value === '') {
            this.displayValue = '';
            this.description = '';
            return true;
        }

        // Check if this is a multi-select value (contains semicolon)
        // If so, we need to handle it with the existing multi-select logic below
        const isMultiSelect = typeof value === 'string' && value.includes(';');
        
        // For object dropdowns, if we have a formatted value (string) and it's NOT multi-select,
        // use it directly. For multi-select, fall through to the existing multi-select handling logic.
        if (typeof value === 'string' && value !== '' && !isMultiSelect) {
            // valueFormatter already formatted it for single-select, use it directly
            this.displayValue = value;
            // For validation, we need to check the raw object against allowedValues
            if (this.allowedValueKey && typeof rawValue === 'object' && rawValue !== null && !Array.isArray(rawValue)) {
                // Single object - validate it
                const matchingOption = DropdownHelperService.findOptionByAllowedValueKey(this.allowedValues, rawValue,
                                                                                         this.allowedValueKey);
                if (matchingOption && this.isListManagerStructure) {
                    this.description = matchingOption.description || '';
                } else {
                    this.description = '';
                }
            } else {
                if (this.isListManagerStructure) {
                    const matchingOption = DropdownHelperService.findOptionByLabel(this.allowedValues, value);
                    this.description = matchingOption?.description || '';
                } else {
                    this.description = '';
                }
            }
            return true;
        }
        
        // For multi-select, use the formatted value for display but validate using the existing logic below
        // The existing multi-select handling will properly validate each individual value

        // If value is still an object (and not multi-select), extract the display value using allowedValueKey
        // Note: For ListManager structures, the value might be a string (the label), not an object
        // For multi-select, we skip this and use the string handling logic below
        if (this.allowedValueKey && typeof rawValue === 'object' && rawValue !== null && !Array.isArray(rawValue) && !isMultiSelect) {
            // Use formatted value if available, otherwise extract from raw object
            if (typeof value === 'string' && value !== '') {
                this.displayValue = value;
            } else {
                this.displayValue = DropdownHelperService.extractValueByKey(rawValue, this.allowedValueKey) || '';
            }
            // Find matching object in allowedValues array (use rawValue, not value)
            const matchingOption = DropdownHelperService.findOptionByAllowedValueKey(this.allowedValues, rawValue,
                                                                                     this.allowedValueKey);
            // For ListManager structure, get description from the object
            if (matchingOption && this.isListManagerStructure) {
                this.description = matchingOption.description || '';
            } else {
                // For regular objects, no description is available
                this.description = '';
            }
        } else if (this.allowedValueKey && this.isListManagerStructure && typeof value === 'string') {
            // Handle object dropdowns with ListManager structure where value is stored as a string (the label)
            // For multi-select, use formatted value for display but raw value for validation
            const valueStr = String(value);
            if (valueStr.includes(';')) {
                // Multi-select: use formatted value for display, but validate using raw value if available
                // If rawValue is an array of objects, use that for validation
                // Otherwise, split the formatted string and validate each part
                let selectedValues: string[];
                if (Array.isArray(rawValue) && rawValue.length > 0) {
                    // Raw value is an array of objects - extract values for validation
                    selectedValues = rawValue.map(obj => 
                        DropdownHelperService.extractValueByKey(obj, this.allowedValueKey) || ''
                    ).filter(v => v !== '');
                    // Use formatted value for display
                    this.displayValue = valueStr;
                } else if (typeof rawValue === 'string' && rawValue.includes(';')) {
                    // Raw value is a semicolon-separated string - use it for validation (no prefix)
                    selectedValues = DropdownHelperService.splitMultiSelectValue(rawValue);
                    // Use formatted value for display
                    this.displayValue = valueStr;
                } else {
                    // Raw value is a single string or formatted value - split and use for both display and validation
                    // Note: If formatted value has a prefix, validation might fail - but this is a fallback
                    selectedValues = DropdownHelperService.splitMultiSelectValue(valueStr);
                    this.displayValue = selectedValues.join('; ');
                }
                const descParts: string[] = [];
                selectedValues.forEach(val => {
                    // Find matching LabelDescription object by allowedValueKey
                    const matchingOption = DropdownHelperService.findOptionByAllowedValueKey(this.allowedValues, val,
                                                                                             this.allowedValueKey);
                    if (matchingOption) {
                        if (matchingOption.description) {
                            descParts.push(matchingOption.description);
                        }
                    }
                });
                this.description = descParts.join('; ');
            } else {
                // Single select
                this.displayValue = valueStr;
                // Find matching LabelDescription object by allowedValueKey
                const matchingOption = DropdownHelperService.findOptionByAllowedValueKey(this.allowedValues, valueStr,
                                                                                         this.allowedValueKey);
                this.description = matchingOption?.description || '';
            }
        } else {
            // For string dropdowns
            // For multi-select, use formatted value for display but raw value for validation
            const valueStr = String(value);
            if (valueStr.includes(';')) {
                // Multi-select: use formatted value for display, but validate using raw value if available
                let selectedValues: string[];
                if (Array.isArray(rawValue) && rawValue.length > 0) {
                    // Raw value is an array - use it directly for validation
                    selectedValues = rawValue.map(v => String(v)).filter(v => v !== '');
                    // Use formatted value for display
                    this.displayValue = valueStr;
                } else if (typeof rawValue === 'string' && rawValue.includes(';')) {
                    // Raw value is a semicolon-separated string - use it for validation (no prefix)
                    selectedValues = DropdownHelperService.splitMultiSelectValue(rawValue);
                    // Use formatted value for display
                    this.displayValue = valueStr;
                } else {
                    // Raw value is a single string or formatted value - split and use for both display and validation
                    // Note: If formatted value has a prefix, validation might fail - but this is a fallback
                    selectedValues = DropdownHelperService.splitMultiSelectValue(valueStr);
                    this.displayValue = selectedValues.join('; ');
                }
                const descParts: string[] = [];
                selectedValues.forEach(val => {
                    if (this.isListManagerStructure) {
                        // Find matching LabelDescription object
                        const matchingOption = DropdownHelperService.findOptionByLabel(this.allowedValues, val);
                        if (matchingOption) {
                            if (matchingOption.description) {
                                descParts.push(matchingOption.description);
                            }
                        }
                    }
                });
                this.description = descParts.join('; ');
            } else {
                // Single select
                this.displayValue = valueStr;
                if (this.isListManagerStructure) {
                    // Find matching LabelDescription object
                    const matchingOption = DropdownHelperService.findOptionByLabel(this.allowedValues, valueStr);
                    this.description = matchingOption?.description || '';
                } else {
                    // For regular arrays, no description available
                    this.description = '';
                }
            }
        }

        return true;
    }
}
